import { IconButton, Box, MenuItem, ToggleButton } from '@mui/material';
import { debounce } from 'lodash';
import { InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import HighlightOnFirstRender from '../HighligtOnFirstRender';
import { DateRangePicker, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { TimeSpan } from '../../../@types/dashboard';
import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths
} from 'date-fns';
import { styled } from '@mui/material/styles';
import useCustomDispatch, { CustomDispachActionPromise } from 'redux/dispatch';
import Icon from 'components/icons/Icon';
import { useTranslator } from 'translation/useTranslator';
import { TextField } from '@nps.today/component-library';

const componentDataTestId = 'from-to-date-picker';

type DateRangeValue<TDate> = [TDate | null, TDate | null];

export const getFromToFilterFromTimespan = (
  timespan: TimeSpan,
  currentFrom?: any,
  currentTo?: any
) => {
  const currentDate = new Date();
  const currentTime = currentDate.getTime();
  const dayInMs = 24 * 60 * 60 * 1000;
  const weekStartsOn = 1;

  let from, to;
  switch (timespan) {
    case TimeSpan.TODAY:
      from = startOfDay(currentDate);
      to = endOfDay(currentDate);
      break;
    case TimeSpan.YESTERDAY:
      const yeasterdayDate = new Date(currentTime - dayInMs);
      from = startOfDay(yeasterdayDate);
      to = endOfDay(yeasterdayDate);
      break;
    case TimeSpan.CURRENT_WEEK:
      from = startOfWeek(currentDate, { weekStartsOn });
      to = endOfWeek(currentDate, { weekStartsOn });
      break;
    case TimeSpan.LAST_WEEK:
      const aWeekAgoDate = new Date(currentTime - dayInMs * 7);
      from = startOfWeek(aWeekAgoDate, { weekStartsOn });
      to = endOfWeek(aWeekAgoDate, { weekStartsOn });
      break;
    case TimeSpan.LAST_30_DAYS:
      const thirtyDaysAgoDate = new Date(currentTime - dayInMs * 30);
      from = startOfDay(thirtyDaysAgoDate);
      to = endOfDay(currentDate);
      break;
    case TimeSpan.CURRENT_MONTH:
      from = startOfMonth(currentDate);
      to = endOfMonth(currentDate);
      break;
    case TimeSpan.LAST_MONTH:
      const aMonthAgoDate = subMonths(currentDate, 1);
      from = startOfMonth(aMonthAgoDate);
      to = endOfMonth(aMonthAgoDate);
      break;
    case TimeSpan.CURRENT_QUARTER:
      from = startOfQuarter(currentDate);
      to = endOfQuarter(currentDate);
      break;
    case TimeSpan.LAST_QUARTER:
      const threeMonthsAgoDate = subMonths(currentDate, 3);
      from = startOfQuarter(threeMonthsAgoDate);
      to = endOfQuarter(threeMonthsAgoDate);
      break;
    case TimeSpan.CURRENT_YEAR:
      from = startOfYear(currentDate);
      to = endOfYear(currentDate);
      break;
    case TimeSpan.LAST_YEAR:
      const aYearAgoDate = new Date(
        currentDate.getFullYear() - 1,
        currentDate.getMonth(),
        currentDate.getDate()
      );
      from = startOfYear(aYearAgoDate);
      to = endOfYear(aYearAgoDate);
      break;
    case TimeSpan.ALL_TIME:
      from = undefined;
      to = undefined;
      break;
    case TimeSpan.CUSTOM:
    default:
      from = currentFrom;
      to = currentTo;
      break;
  }

  return { from, to };
};

type FromToDatePickerProps = {
  timespan: TimeSpan;
  toDateFilter: number | undefined;
  fromDateFilter: number | undefined;
  updateDateFilter: ({
    from,
    to,
    timespan
  }: {
    from: any;
    to: any;
    timespan: TimeSpan;
  }) => () => CustomDispachActionPromise;
  maxTimeSpan?: TimeSpan;
  shouldUpdate?: number; //any changes to this value, triggers an update of the to and from dates according to the selected timespan
  enableDynamicWidth?: boolean;
  dataTestId: string;
};

const FromToDatePicker = ({
  timespan,
  toDateFilter,
  fromDateFilter,
  updateDateFilter,
  maxTimeSpan,
  shouldUpdate,
  enableDynamicWidth,
  dataTestId
}: FromToDatePickerProps) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const [dateRange, setDateRange] = useState<DateRangeValue<Date>>([
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null
  ]);

  const [displayCustomDateRange, setDisplayCustomDateRange] = useState(
    timespan === TimeSpan.CUSTOM
  );

  const debouncedUpdateDateFilter = useRef(
    debounce(async ({ from, to, timespan }: { from: any; to: any; timespan: TimeSpan }) => {
      customDispatch({
        action: updateDateFilter,
        actionParameters: { from, to, timespan },
        disableAllMessages: true
      });
    }, 300)
  ).current;

  //ref for saving the crurrent active dateRange. If the input is invalid onBlur, set this to the input values.
  // used as a ref, as the dateFilter is debounced and the dateRange is updated on change
  const currentActiveDateRangeRef = useRef<DateRangeValue<Date>>(dateRange);
  const handleDateRangeChange = useRef(
    ({ from, to, timespan }: { from: any; to: any; timespan: TimeSpan }) => {
      debouncedUpdateDateFilter({ from, to, timespan });
    }
  ).current;

  const prevTimespan = useRef<TimeSpan | undefined>(undefined);
  useEffect(() => {
    if (timespan === TimeSpan.CUSTOM) return;
    if (prevTimespan.current === timespan) return;
    prevTimespan.current = timespan;
    const { from, to } = getFromToFilterFromTimespan(timespan);
    const nextRange: DateRangeValue<Date> = [
      from ? new Date(from) : null,
      to ? new Date(to) : null
    ];
    currentActiveDateRangeRef.current = nextRange;
    setDateRange(nextRange);

    handleDateRangeChange({
      from,
      to,
      timespan
    });
  }, [timespan, shouldUpdate, handleDateRangeChange]);

  let timeSpanArray = Object.values(TimeSpan);
  let indexOfMaxTimeSpan;
  if (maxTimeSpan) {
    indexOfMaxTimeSpan = timeSpanArray.indexOf(maxTimeSpan);
  }
  timeSpanArray = timeSpanArray.splice(0, indexOfMaxTimeSpan || timeSpanArray.length);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '1rem',
        width: enableDynamicWidth ? 'auto' : 'max-content'
      }}
    >
      {!displayCustomDateRange && (
        <HighlightOnFirstRender enabled={timespan !== TimeSpan.CUSTOM}>
          <TextField
            dataTestId={`${dataTestId}-${componentDataTestId}-timespan`}
            value={timespan || TimeSpan.LAST_30_DAYS}
            onChange={(event: any) => {
              const newTimespan = event.target.value as TimeSpan;
              const { from, to } = getFromToFilterFromTimespan(
                newTimespan,
                fromDateFilter,
                toDateFilter
              );
              setDateRange([from ? new Date(from) : null, to ? new Date(to) : null]);
              if (newTimespan === TimeSpan.CUSTOM) {
                setDisplayCustomDateRange(true);
              }
              customDispatch({
                action: updateDateFilter,
                actionParameters: { from, to, timespan: newTimespan },
                disableAllMessages: true
              });
            }}
            select
            label="Timespan"
          >
            {timeSpanArray.map((timespan, index) => (
              <MenuItem key={index} value={timespan}>
                {timespan}
              </MenuItem>
            ))}
          </TextField>
        </HighlightOnFirstRender>
      )}

      <CalenderPickerWrapper
        displaydatepicker={displayCustomDateRange.toString()}
        width={enableDynamicWidth ? 'auto' : 'max-content'}
      >
        {displayCustomDateRange && (
          <DateRangePicker
            format="dd/MM/yyyy"
            value={dateRange}
            onChange={(newValue) => {
              //update local state
              currentActiveDateRangeRef.current = newValue;
              setDateRange(newValue);

              const startDate = newValue[0] ? startOfDay(newValue[0] as Date) : undefined;
              const endDate = newValue[1] ? endOfDay(newValue[1] as Date) : undefined;

              // If both of the dates are defined, check if startDate is before endDate
              if (startDate && endDate && isBefore(endDate, startDate)) {
                return;
              }

              handleDateRangeChange({
                from: startDate?.getTime?.() || undefined,
                to: endDate?.getTime?.() || undefined,
                timespan: TimeSpan.CUSTOM
              });
            }}
            slots={{ field: MultiInputDateRangeField }}
            slotProps={{
              textField: (params: any) => {
                const isFromField = params.position === 'start';
                const [startValue, endValue] = currentActiveDateRangeRef.current;
                const hasValue = Boolean(isFromField ? startValue : endValue);
                return {
                  label: isFromField ? translator.startDate() : translator.endDate(),
                  inputProps: {
                    ...params.inputProps,
                    'data-testid': `${dataTestId}-${componentDataTestId}-${
                      isFromField ? 'from' : 'to'
                    }-text-field`
                  } as InputHTMLAttributes<HTMLInputElement>,
                  InputProps: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {hasValue && (
                          <IconButton
                            onClick={() => {
                              const nextRange: DateRangeValue<Date> = isFromField
                                ? [null, endValue]
                                : [startValue, null];
                              currentActiveDateRangeRef.current = nextRange;
                              setDateRange(nextRange);
                              handleDateRangeChange({
                                from: nextRange[0]?.getTime?.() || undefined,
                                to: nextRange[1]?.getTime?.() || undefined,
                                timespan: TimeSpan.CUSTOM
                              });
                            }}
                          >
                            <Icon type="clear" />
                          </IconButton>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    )
                  },
                  sx: { maxWidth: 'none', width: 'max-content' }
                };
              }
            }}
          />
        )}
        <ToggleButton
          value={displayCustomDateRange}
          selected={displayCustomDateRange}
          onClick={() => setDisplayCustomDateRange((prevState) => !prevState)}
        >
          <Icon type="calendar" />
        </ToggleButton>
      </CalenderPickerWrapper>
    </Box>
  );
};

export default FromToDatePicker;

const CalenderPickerWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'displaydatepicker' && prop !== 'width'
})<{ displaydatepicker: string; width: string }>(({ theme, displaydatepicker, width }) => ({
  display: 'flex',
  position: 'relative',
  gap: '1rem',
  width: width,
  '&:before': {
    position: 'absolute',
    content: "''",
    width: '100%',
    borderRadius: '.05rem',
    height: '.1rem',
    backgroundColor: theme.palette.primary.main,
    right: 0,
    top: 'calc(100% + .5rem)',
    transform: 'translateY(-50%)',
    opacity: displaydatepicker === 'true' ? 0.5 : 0,
    transition: 'opacity .5s ease'
  }
}));
